import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { IExtraInfo, ILayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import {
    isEditAIPPTComponent as isEditAIPPTComponentFunc,
    isEditAIPPTTemplate as isEditAIPPTTemplateFunc,
} from '@/hooks/fetch-template/editor-mode';
import { useStoreState } from '@/store';
import SelectTag, { ISelectTypeListItem } from './select-tag';
import {
    getTextLayerTagList,
    groupLayerTagList,
    imgLayerTagList,
    pageTagList,
    svgLayerTagList,
} from './tag-list-config';

const isEditAIPPTComponent = isEditAIPPTComponentFunc();
const isEditAIPPTTemplate = isEditAIPPTTemplateFunc();

const getTextExtraInfo = (label: { [key: string]: any }, textLayerTagList: ISelectTypeListItem[]) => {
    const { type, fill, content_index, gradient_color, textExpend } = label;

    if (type) {
        const textType = textLayerTagList.find((item) => item.name === '类型');
        const item = textType?.list.find((item) => item.name === type);
        item && (item.isSelect = true);
    }

    if (fill) {
        const textColor = textLayerTagList.find((item) => item.name === '主题色');
        const item = textColor?.list.find((item) => item.name === fill);
        item && (item.isSelect = true);
    }

    if (gradient_color !== undefined) {
        const gradientTextColor = textLayerTagList.find((item) => item.name === '渐变');

        gradientTextColor && gradient_color && (gradientTextColor.list[0].isSelect = true);
        gradientTextColor && !gradient_color && (gradientTextColor.list[1].isSelect = true);
    }

    if (content_index) {
        const textIndex = textLayerTagList.find((item) => item.name === '序号');
        const item = textIndex?.list.find((item) => item.name === content_index.toString());
        item && (item.isSelect = true);
    }

    if (textExpend) {
        const textExpendList = textLayerTagList.find((item) => item.name === '拓展');
        const item = textExpendList?.list.find((item) => item.name === textExpend);
        item && (item.isSelect = textExpend);
    }
};

export const getImageExtraInfo = (label: { [key: string]: any }, imgLayerTagList: ISelectTypeListItem[]) => {
    const { type, content_index } = label;

    if (type === '可替换图片') {
        imgLayerTagList[0].list[0].isSelect = true;
    } else {
        imgLayerTagList[0].list[1].isSelect = false;
    }

    if (content_index) {
        const textIndex = imgLayerTagList.find((item) => item.name === '序号');
        const item = textIndex?.list.find((item) => item.name === content_index.toString());
        item && (item.isSelect = true);
    }
};

export const getVectorExtraInfo = (label: { [key: string]: any }, svgLayerTagList: ISelectTypeListItem[]) => {
    const { fill, gradient_color, svgExpend, stroke_color, stroke_gradient } = label;

    if (fill) {
        const textColor = svgLayerTagList.find((item) => item.name === '主题色');
        const item = textColor?.list.find((item) => item.name === fill);
        item && (item.isSelect = true);
    }

    if (gradient_color !== undefined) {
        const gradientTextColor = svgLayerTagList.find((item) => item.name === '填充渐变');

        gradientTextColor && gradient_color && (gradientTextColor.list[0].isSelect = true);
        gradientTextColor && !gradient_color && (gradientTextColor.list[1].isSelect = true);
    }

    if (svgExpend !== undefined) {
        const svgExpendList = svgLayerTagList.find((item) => item.name === '可拓展');

        svgExpendList && svgExpend && (svgExpendList.list[0].isSelect = true);
        svgExpendList && !svgExpend && (svgExpendList.list[1].isSelect = true);
    }

    if (stroke_color) {
        const strokeColorList = svgLayerTagList.find((item) => item.name === '描边色');
        const item = strokeColorList?.list.find((item) => item.name === stroke_color);
        item && (item.isSelect = true);
    }

    if (stroke_gradient !== undefined) {
        const gradientStrokeColor = svgLayerTagList.find((item) => item.name === '描边渐变');

        gradientStrokeColor && stroke_gradient && (gradientStrokeColor.list[0].isSelect = true);
        gradientStrokeColor && !stroke_gradient && (gradientStrokeColor.list[1].isSelect = true);
    }
};

export const getGroupExtraInfo = (label: { [key: string]: any }, groupLayerTagList: ISelectTypeListItem[]) => {
    const { moveable } = label;

    if (moveable) {
        const moveableList = groupLayerTagList.find((item) => item.name === '是否可移动');
        moveableList && (moveableList.list[0].isSelect = true);
    }
};

export const getPageExtraInfo = (label: { [key: string]: any }, pageTagList: ISelectTypeListItem[]) => {
    if (!label) {
        return;
    }
    const { type, theme, layout } = label;

    if (type) {
        const pageTypeList = pageTagList.find((item) => item.name === '页面类型');
        const item = pageTypeList?.list.find((item) => item.name === type);
        item && (item.isSelect = true);
    } else {
        const pageTypeList = pageTagList.find((item) => item.name === '页面类型');
        const item = pageTypeList?.list.find((item) => item.name === '内容');
        item && (item.isSelect = true);
    }

    if (theme) {
        const pageTypeList = pageTagList.find((item) => item.name === '色调');

        const item = pageTypeList?.list.find((item) => item.name === theme);
        item && (item.isSelect = true);
    }

    if (layout) {
        const pageTypeList = pageTagList.find((item) => item.name === '布局');

        const item = pageTypeList?.list.find((item) => item.name === layout);
        item && (item.isSelect = true);
    }
};

const useGetCurrentTypeList = (editor: Editor, isGroup = false) => {
    const [selectTypeList, setSelectTypeList] = useState<ISelectTypeListItem[]>([]);

    const layerInfo = editor.active.getPick();

    const templateExtraInfo = useStoreState((state) => state.templateModel.extraInfo);
    const stageIndex = editor.active.getActiveStageIndex();
    useEffect(() => {
        const extraInfo = layerInfo?.extraInfo;
        let theme = '';
        if (layerInfo?.parentId) {
            const parentLayer = editor.stage.getLayer(layerInfo.parentId);
            if (parentLayer && (parentLayer.title === '深色模式' || parentLayer.title === '浅色模式')) {
                theme = parentLayer.title;
            }
        }
        if (layerInfo?.layerType === LayerType.TEXT_LAYER) {
            const list = cloneDeep(getTextLayerTagList(templateExtraInfo?.componentType));
            if (!extraInfo || Object.keys(extraInfo || []).length === 0) {
                setSelectTypeList(list);
            } else {
                const { label } = extraInfo;
                if (label) {
                    getTextExtraInfo(label, list);
                }

                setSelectTypeList(list);
            }
        } else if (layerInfo?.layerType === LayerType.VECTOR_LAYER || layerInfo?.layerType === LayerType.BRUSH_LAYER) {
            const list = cloneDeep(svgLayerTagList) as ISelectTypeListItem[];

            if (!extraInfo || Object.keys(extraInfo || []).length === 0) {
                setSelectTypeList(list);
            } else {
                const { label } = extraInfo;
                getVectorExtraInfo(label, list);
                if (theme) {
                    list.find((item) => {
                        if (item.name === '主题色') {
                            item.theme = theme;
                        }
                    });
                }

                setSelectTypeList(list);
            }
        } else if (layerInfo?.layerType === LayerType.IMAGE_LAYER) {
            const list = cloneDeep(imgLayerTagList);
            if (!extraInfo || Object.keys(extraInfo || []).length === 0) {
                setSelectTypeList(list);
            } else {
                const { label } = extraInfo;
                getImageExtraInfo(label, list);
                setSelectTypeList(list);
            }
        } else if (layerInfo?.layerType === LayerType.GROUP_LAYER && isGroup) {
            const list = cloneDeep(groupLayerTagList);
            if (!extraInfo || Object.keys(extraInfo || []).length === 0) {
                setSelectTypeList(list);
            } else {
                const { label } = extraInfo;
                getGroupExtraInfo(label, list);
                setSelectTypeList(list);
            }
        } else if (!layerInfo && isEditAIPPTTemplate) {
            const list = cloneDeep(pageTagList);
            const extraInfo = editor.stage.getExtraInfo(stageIndex);
            if (!extraInfo || !extraInfo.extraInfo || Object.keys(extraInfo.extraInfo || []).length === 0) {
                setSelectTypeList(list);
            } else {
                const { extraInfo: pageExtraInfo } = extraInfo;

                if (pageExtraInfo) {
                    const { label } = pageExtraInfo;
                    getPageExtraInfo(label, list);
                    setSelectTypeList(list);
                }
            }
        }
    }, [layerInfo?.id, stageIndex, templateExtraInfo?.componentType]);

    return selectTypeList;
};

const PPTMarkPanel = ({ isGroup = false, editor }: { isGroup?: boolean; editor: Editor }) => {
    const layerInfo = editor.active.getPick();

    const originCurrentTypeList = useGetCurrentTypeList(editor, isGroup);
    const stageIndex = editor.active.getActiveStageIndex();
    const [selectTypeList, setSelectTypeList] = useState<ISelectTypeListItem[]>(originCurrentTypeList);
    const [currentExtraInfo, setCurrentExtraInfo] = useState<IExtraInfo | null>(null);
    const typeListChange = (typeList: ISelectTypeListItem[]) => {
        setSelectTypeList(typeList);

        if (!layerInfo && isEditAIPPTTemplate) {
            const extraInfo = editor.stage.getExtraInfo(stageIndex);
            const { extraInfo: pageExtraInfo } = extraInfo;
            if (pageExtraInfo) {
                const { label } = pageExtraInfo;
                if (!label) {
                    pageExtraInfo.label = {};
                    pageExtraInfo.label.type = typeList[0].list.find((item) => item.isSelect)?.name || '';
                    pageExtraInfo.label.theme = typeList[1].list.find((item) => item.isSelect)?.name;
                    pageExtraInfo.label.layout = typeList[2].list.find((item) => item.isSelect)?.name;
                } else {
                    label.type = typeList[0].list.find((item) => item.isSelect)?.name || '';
                    label.theme = typeList[1].list.find((item) => item.isSelect)?.name;
                    label.layout = typeList[2].list.find((item) => item.isSelect)?.name;
                }
            } else {
                extraInfo.extraInfo = {
                    label: {
                        type: typeList[0].list.find((item) => item.isSelect)?.name || '',
                        theme: typeList[1].list.find((item) => item.isSelect)?.name,
                        layout: typeList[2].list.find((item) => item.isSelect)?.name,
                    },
                };
            }
            editor.stage.setExtraInfo(stageIndex, { ...extraInfo });
        } else if (layerInfo && layerInfo.layerType === LayerType.TEXT_LAYER) {
            if (!layerInfo.extraInfo) {
                layerInfo.extraInfo = {};
            }

            const { extraInfo } = layerInfo;
            if (!extraInfo.label) {
                extraInfo.label = {};
            }

            const { label } = extraInfo;
            typeList.forEach((item) => {
                if (item.name === '主题色') {
                    label.fill = item.list.find((subItem) => subItem.isSelect)?.name;
                } else if (item.name === '类型') {
                    label.type = item.list.find((subItem) => subItem.isSelect)?.name;
                } else if (item.name === '拓展') {
                    label.textExpend = item.list.find((subItem) => subItem.isSelect)?.name;
                } else if (item.name === '渐变') {
                    label.gradient_color = item.list[0].isSelect;
                } else if (item.name === '序号') {
                    label.content_index = item.list.find((subItem) => subItem.isSelect)?.name;
                }
            });

            editor.stage.updateLayer({ ...layerInfo });
        } else if (layerInfo?.layerType === LayerType.VECTOR_LAYER || layerInfo?.layerType === LayerType.BRUSH_LAYER) {
            if (!layerInfo.extraInfo) {
                layerInfo.extraInfo = {};
            }

            const { extraInfo } = layerInfo;
            if (!extraInfo.label) {
                extraInfo.label = {};
            }

            const { label } = extraInfo;
            typeList.forEach((item) => {
                if (item.name === '主题色') {
                    label.fill = item.list.find((subItem) => subItem.isSelect)?.name;
                } else if (item.name === '可拓展') {
                    label.svgExpend = item.list[0].isSelect;
                } else if (item.name === '填充渐变') {
                    label.gradient_color = item.list[0].isSelect;
                } else if (item.name === '描边色') {
                    label.stroke_color = item.list.find((subItem) => subItem.isSelect)?.name;
                } else if (item.name === '描边渐变') {
                    label.stroke_gradient = item.list[0].isSelect;
                }
            });

            editor.stage.updateLayer({ ...layerInfo });
        } else if (layerInfo?.layerType === LayerType.IMAGE_LAYER) {
            if (!layerInfo.extraInfo) {
                layerInfo.extraInfo = {};
            }

            const { extraInfo } = layerInfo;
            if (!extraInfo.label) {
                extraInfo.label = {};
            }

            const { label } = extraInfo;

            typeList.forEach((item) => {
                if (item.name === '可替换图片') {
                    label.type = typeList[0].list[0].isSelect ? '可替换图片' : undefined;
                } else if (item.name === '序号') {
                    label.content_index = item.list.find((subItem) => subItem.isSelect)?.name;
                }
            });

            editor.stage.updateLayer({ ...layerInfo });
        } else if (layerInfo?.layerType === LayerType.GROUP_LAYER && isGroup) {
            if (!layerInfo.extraInfo) {
                layerInfo.extraInfo = {};
            }

            const { extraInfo } = layerInfo;
            if (!extraInfo.label) {
                extraInfo.label = {};
            }

            const { label } = extraInfo;
            typeList.forEach((item) => {
                if (item.name === '是否可移动') {
                    label.moveable = item.list[0].isSelect;
                }
            });

            editor.stage.updateLayer({ ...layerInfo });
        }
    };

    useEffect(() => {
        setSelectTypeList(originCurrentTypeList);
    }, [originCurrentTypeList]);

    useEffect(() => {
        editor.stage.onLayerChange(() => {
            const stageExtraInfo = editor.stage.getExtraInfo(stageIndex);
            setCurrentExtraInfo(stageExtraInfo);
        });

        const stageExtraInfo = editor.stage.getExtraInfo(stageIndex);
        setCurrentExtraInfo(stageExtraInfo);
    }, []);

    let chapterValues = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10'] as string[];
    if (!layerInfo && currentExtraInfo?.extraInfo?.label?.type === '章节') {
        const stage = editor.stage.getStageByIndex(stageIndex);
        traverseTrees(stage, (layer: ILayerProps) => {
            if (layer.extraInfo?.label?.type === '章节序号') {
                const values = layer.extraInfo?.label?.chapter_index_values;
                if (values && values.length > 0) {
                    chapterValues = values;
                }
            }
        });
    }

    return (
        <div>
            <SelectTag selectTypeList={selectTypeList} setSelectTypeList={typeListChange} />

            {!layerInfo && currentExtraInfo?.extraInfo?.label?.type === '章节' ? (
                <div className="pptMark-chapter-index">
                    <div className="title">章节序号：</div>
                    {chapterValues.map((item, index) => {
                        return (
                            <div key={index} className="item">
                                <span className="label">序号{index + 1}</span>
                                <input
                                    value={item}
                                    onChange={(e) => {
                                        if (!e.target.value) {
                                            return;
                                        }
                                        const stage = editor.stage.getStageByIndex(stageIndex);
                                        traverseTrees(stage, (layer: ILayerProps) => {
                                            if (layer.extraInfo?.label?.type === '章节序号') {
                                                if (
                                                    !layer.extraInfo?.label?.chapter_index_values ||
                                                    layer.extraInfo?.label?.chapter_index_values?.length === 0
                                                ) {
                                                    layer.extraInfo.label.chapter_index_values = [
                                                        '01',
                                                        '02',
                                                        '03',
                                                        '04',
                                                        '05',
                                                        '06',
                                                        '07',
                                                        '08',
                                                        '09',
                                                        '10',
                                                    ];
                                                }
                                                layer.extraInfo.label.chapter_index_values[index] = e.target.value;
                                                editor.stage.updateLayer({ ...layer });
                                            }
                                        });
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

export default PPTMarkPanel;
